import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  useTheme,
  Fade,
  Slide,
  Grid,
  Divider,
  Avatar,
  Step,
  StepLabel,
  Stepper,
  StepContent,
  InputAdornment,
} from '@mui/material';
import {
  DeleteForever,
  AdminPanelSettings,
  Send,
  Info,
  Warning,
  ContactSupport,
  ArrowBack,
  Business,
  Security,
  Schedule,
  Email,
  CheckCircle,
  Phone,
  Policy,
  Person,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { userService } from '../../services/apiServices';

const validationSchema = yup.object({
  email: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  reason: yup
    .string('Enter reason for recovery')
    .min(10, 'Please provide at least 10 characters explaining your reason')
    .required('Reason is required'),
});

const AccountDeleted = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(true);

  // Only prefill username if coming from legitimate sources (must be before useFormik)
  const message = location.state?.message || 'Your account has been deleted.';
  const userName = location.state?.userName || ''; // Only available from legitimate redirects
  
  // Encode username in base64 for security
  const encodeUsername = (username) => {
    if (!username) {
      console.warn('Username is empty, cannot encode');
      return '';
    }
    try {
      const encoded = btoa(username); // Base64 encode
      console.log('Encoded username:', encoded); // Debug log
      return encoded;
    } catch (error) {
      console.error('Error encoding username:', error);
      return '';
    }
  };

  // Debug logs
  useEffect(() => {
    console.log('Location state:', location.state);
    console.log('Username from location:', userName);
    console.log('Encoded username:', encodeUsername(userName));
  }, [location.state, userName]);

  const formik = useFormik({
    initialValues: {
      userName: userName,
      userToken: encodeUsername(userName), // Base64 encoded username
      email: '',
      reason: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Validation check before sending
      if (!values.userToken) {
        setError('Username token is missing. Please ensure you accessed this page from a valid login attempt.');
        setLoading(false);
        return;
      }
      
      try {
        // Send base64 encoded username instead of plain username
        const payload = {
          userToken: values.userToken, // Base64 encoded username
          email: values.email,
          reason: values.reason,
        };
        
        console.log('Sending payload:', payload); // Debug log
        
        const response = await userService.requestAccountRecovery(payload);
        
        if (response.data.success) {
          setSuccess('Your account recovery request has been submitted successfully. Our admin team will review your request and contact you via email within 3-5 business days.');
          formik.resetForm({ values: { userName: userName, userToken: encodeUsername(userName), email: '', reason: '' } });
        } else {
          setError(response.data.message || 'Failed to submit recovery request');
        }
      } catch (err) {
        console.error('Recovery request error:', err);
        const errorMessage = err.response?.data?.message || 'Failed to submit recovery request. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Security check - ensure legitimate access to this page
  useEffect(() => {
    const hasValidAccess = location.state?.fromDeletedLogin === true || 
                          location.state?.fromAccountDeletion === true ||
                          location.state?.userName; // Coming from login with deleted account
    
    // Additional check for session-based access token
    const sessionAccess = sessionStorage.getItem('accountDeletedAccess');
    
    if (!hasValidAccess && !sessionAccess) {
      // Redirect unauthorized users back to login with a message
      console.warn('Unauthorized access attempt to AccountDeleted page');
      navigate('/auth/login', { 
        replace: true,
        state: { 
          message: 'Access denied. Please login to access account recovery.',
          severity: 'warning'
        }
      });
      return;
    }
    
    // Clear session access token after use (one-time access)
    if (sessionAccess) {
      sessionStorage.removeItem('accountDeletedAccess');
    }
    
    // Mark validation as complete
    setIsValidating(false);
  }, [location.state, navigate]);

  // Show loading while validating access
  if (isValidating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #263238 0%, #37474f 50%, #455a64 100%)'
            : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: 'auto',
        display: 'flex',
        position: 'relative',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0a0e27 0%, #1a237e 50%, #3949ab 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundAttachment: 'fixed',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="2"/%3E%3Ccircle cx="37" cy="7" r="2"/%3E%3Ccircle cx="7" cy="37" r="2"/%3E%3Ccircle cx="37" cy="37" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        },
        overflow: 'auto',
      }}
    >
      <Container 
        component="main" 
        maxWidth="xl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: { xs: 2, sm: 3, lg: 4 },
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              md: '1fr', 
              lg: '1.2fr 1fr',
              xl: '1.3fr 1fr'
            },
            gap: { xs: 0, lg: 6, xl: 8 },
            width: '100%',
            maxWidth: { xs: '500px', lg: '1200px', xl: '1400px' },
            alignItems: 'center',
            mx: 'auto',
          }}
        >
          {/* Left Side - Welcome Content */}
          <Fade in timeout={1000}>
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'white',
                pr: { lg: 3, xl: 4 },
                minHeight: { lg: '600px' },
              }}
            >
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h2"
                  component="h1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Account Recovery
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontWeight: 300,
                    lineHeight: 1.4,
                  }}
                >
                  Enterprise-grade account recovery system with secure verification and admin oversight
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Security sx={{ color: '#4fc3f7', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Secure Process
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Multi-step verification with admin oversight for security
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Schedule sx={{ color: '#81c784', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Quick Response
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        3-5 business day SLA for account recovery requests
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Card
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Business sx={{ color: '#ffb74d', mr: 2, fontSize: 28 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Enterprise Support
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Professional support team available for assistance
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Fade>

          {/* Right Side - Recovery Form */}
          <Slide direction="left" in timeout={800}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Paper
                elevation={24}
                sx={{
                  p: { xs: 3, sm: 4, lg: 5 },
                  borderRadius: 4,
                  backdropFilter: 'blur(20px)',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(255, 255, 255, 0.95)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 20px 40px rgba(0, 0, 0, 0.4)'
                    : '0 20px 40px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                  width: '100%',
                  maxWidth: { xs: '400px', sm: '450px', lg: '500px' },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 25px 50px rgba(0, 0, 0, 0.5)'
                      : '0 25px 50px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Logo and Title */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      p: 2,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                      boxShadow: '0 8px 24px rgba(220, 38, 38, 0.3)',
                    }}
                  >
                    <DeleteForever sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Typography
                    component="h1"
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(45deg, #f87171 30%, #dc2626 90%)'
                        : 'linear-gradient(45deg, #dc2626 30%, #ef4444 90%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 1,
                    }}
                  >
                    Account Deleted
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 350, mx: 'auto', lineHeight: 1.6 }}
                  >
                    {message} Submit a recovery request below to restore your account access.
                  </Typography>
                </Box>

                {/* Alert Messages */}
                {error && (
                  <Slide direction="down" in={!!error}>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        width: '100%', 
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.2rem',
                        },
                      }}
                      onClose={() => setError('')}
                    >
                      {error}
                    </Alert>
                  </Slide>
                )}

                {success && (
                  <Slide direction="down" in={!!success}>
                    <Alert 
                      severity="success" 
                      sx={{ 
                        width: '100%', 
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.2rem',
                        },
                      }}
                      onClose={() => setSuccess('')}
                    >
                      {success}
                    </Alert>
                  </Slide>
                )}

                {/* Recovery Form */}
                <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    id="userName"
                    name="userName"
                    label="Username"
                    placeholder={userName ? "Username verified" : "Username not available - please contact admin"}
                    value={formik.values.userName}
                    disabled={true} // Always disabled for security
                    margin="normal"
                    error={!userName} // Show error styling if username is missing
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color={userName ? "action" : "error"} />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)',
                        '&.Mui-disabled': {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.03)' 
                            : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&:hover': {
                          boxShadow: 'none', // Remove hover effect for disabled field
                        },
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.5)' 
                          : 'rgba(0, 0, 0, 0.6)',
                      },
                    }}
                    helperText={
                      userName 
                        ? "Username is pre-filled for security" 
                        : "Username not available. Please ensure you accessed this page from a valid login attempt."
                    }
                  />

                  {/* Hidden field for base64 encoded username */}
                  <input
                    type="hidden"
                    name="userToken"
                    value={formik.values.userToken}
                  />

                  {/* Show warning if no username available */}
                  {!userName && (
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        mb: 3,
                        borderRadius: 2,
                      }}
                    >
                      Username not detected. Please navigate to this page from a deleted account login attempt or contact system administrator.
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Corporate Email Address"
                    type="email"
                    placeholder="Enter your business email address"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    disabled={loading}
                    margin="normal"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    id="reason"
                    name="reason"
                    label="Business Justification"
                    multiline
                    rows={4}
                    value={formik.values.reason}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.reason && Boolean(formik.errors.reason)}
                    helperText={formik.touched.reason && formik.errors.reason}
                    disabled={loading}
                    placeholder="Please provide a detailed business justification for account recovery..."
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !userName || !formik.values.userToken}
                    startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                    sx={{
                      mt: 2,
                      mb: 2,
                      py: 1.5,
                      borderRadius: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)'
                          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        transform: 'translateY(-1px)',
                      },
                      '&:disabled': {
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(100, 116, 139, 0.3)'
                          : 'rgba(100, 116, 139, 0.3)',
                        transform: 'none',
                      },
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {loading ? 'Processing Request...' : 
                     !userName ? 'Username Required' :
                     'Submit Recovery Request'}
                  </Button>

                  <Divider sx={{ my: 3 }} />

                  {/* Back to Login */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/auth/login"
                      variant="text"
                      startIcon={<ArrowBack />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                        '&:hover': {
                          background: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                        },
                      }}
                    >
                      Return to Login Portal
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Slide>
        </Box>

        {/* Bottom Information Panel - Mobile Only */}
        <Box
          sx={{
            display: { xs: 'block', lg: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: theme.palette.mode === 'dark'
              ? 'rgba(15, 23, 42, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderTop: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(71, 85, 105, 0.3)' 
              : 'rgba(203, 213, 225, 0.5)'}`,
            p: 2,
            zIndex: 1000,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            Response Time: 3-5 Business Days • Enterprise Security • 24/7 Support
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AccountDeleted;